import { watchEffect, type MaybeRefOrGetter, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { effectiveTitleKey } from '@/router/routes'
import { useAuthStore } from '@/stores/auth'

/**
 * Keeps `document.title` in sync with the active route **and** the active
 * locale. Doing it in a watchEffect rather than in the router's `afterEach`
 * matters: switching language does not trigger a navigation, so an afterEach
 * hook would leave a stale, wrongly-localised title behind.
 *
 * Menu-driven pages are skipped: only `DynamicMenuView` knows the resolved
 * label for the entry it is rendering, so it sets its own title.
 *
 * @param notFound quando è vero la pagina sta mostrando il 404 al posto della
 *                 view (rotta del pannello aperta da un utente qualunque): il
 *                 titolo deve dire "non trovata", non il nome della sezione —
 *                 altrimenti il tab del browser rivelerebbe ciò che la pagina
 *                 sta nascondendo.
 */
export function useDocumentTitle(notFound?: MaybeRefOrGetter<boolean>): void {
  const route = useRoute()
  const { t, locale } = useI18n()
  const auth = useAuthStore()

  watchEffect(() => {
    // Referenced explicitly so the effect re-runs when the language changes.
    void locale.value

    const appName = import.meta.env.VITE_APP_NAME || 'Travick'

    if (toValue(notFound) === true) {
      document.title = `${t('notFound.title')} · ${appName}`
      return
    }

    // `/` is titled differently for a guest and a user, so this re-runs on
    // login and logout too.
    const titleKey = effectiveTitleKey(route.meta, auth.isAuthenticated)

    if (typeof titleKey === 'string') {
      document.title = `${t(titleKey)} · ${appName}`
    } else if (route.name !== 'dynamic') {
      document.title = appName
    }
  })
}
